import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { AsyncStateNotification } from '@ui/notifications';
import { createAsyncSelectors } from '@lib/selectors';
import { FeatureFlagCard } from './FeatureFlagCard';
import { FeatureFlagLabel } from '@sonar-software/sonar-core';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { objectKeys } from '@sonar-software/sonar-ts-lib';
import { SearchInput } from '@ui/general';
import { SonarTable } from '@ui/SonarTable';
import { VerticalColumn } from '@ui/general/VerticalColumn';
import { VerticalColumnView } from '@ui/general/VerticalColumnView';
import { adminActions } from '@sonar-software/sonar-admin';
import { getFeatureFlagTableColumns } from './FeatureFlagTableColumns';
import React, { useCallback, useMemo, useState } from 'react';
import { useAction, useAsyncState, useOnMount } from '@hooks';
const featureFlagSelectors = createAsyncSelectors((state) => state.admin.featureFlag.get);
const overrideDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.override);
const setDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.useDefault);
const postFeatureFlagSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.patch);
const lowercaseFeatureFlags = objectKeys(FeatureFlagLabel).reduce((obj, key) => {
    obj[key.toLocaleLowerCase()] = FeatureFlagLabel[key];
    return obj;
}, {});
function getData(data) {
    if (!data || data === null || data.featureFlags.length === 0) {
        return [];
    }
    const orgByGroup = data.featureFlags
        .reduce((hashmap, curr) => {
        var _a, _b, _c, _d, _e;
        let enabledCustomers = 0;
        if (curr.state === true) {
            enabledCustomers = data.totalCustomerCount;
        }
        else if (curr.state === false) {
            enabledCustomers = 0;
        }
        else {
            enabledCustomers = curr.enabledByDefault ?
                data.totalCustomerCount - curr.exceptionCustomers
                    .filter((x) => x.enabled !== curr.enabledByDefault).length :
                curr.exceptionCustomers.filter((x) => x.enabled).length;
        }
        if (curr.groupId) {
            if (!hashmap[curr.groupId]) {
                hashmap[curr.groupId] = {
                    children: [],
                    id: curr.groupId,
                    label: (_a = curr.groupName) !== null && _a !== void 0 ? _a : ''
                };
            }
            (_c = (_b = hashmap[curr.groupId]) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c.push({
                count: data.totalCustomerCount === enabledCustomers ? 'All' : enabledCustomers,
                enabledByDefault: curr.enabledByDefault,
                id: curr.name,
                label: (_d = lowercaseFeatureFlags[curr.name.toLowerCase()]) !== null && _d !== void 0 ? _d : curr.name,
                state: curr.state
            });
        }
        else {
            hashmap[curr.name] = {
                count: data.totalCustomerCount === enabledCustomers ? 'All' : enabledCustomers,
                enabledByDefault: curr.enabledByDefault,
                id: curr.name,
                label: (_e = lowercaseFeatureFlags[curr.name.toLowerCase()]) !== null && _e !== void 0 ? _e : curr.name,
                state: curr.state
            };
        }
        return hashmap;
    }, {});
    return Object.values(orgByGroup);
}
export const FeatureFlagsView = () => {
    const load = useAction(adminActions.requestGetAdminFeatureFlags);
    const [filter, setFilter] = useState('');
    const { data, isRequested } = useAsyncState(featureFlagSelectors);
    const [editRecordId, setEditRecordId] = useState(null);
    const handleOnClick = useCallback((name) => {
        var _a, _b;
        setEditRecordId((_b = (_a = data === null || data === void 0 ? void 0 : data.featureFlags.find((record) => record.name === name)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : null);
    }, [data]);
    const columns = useMemo(() => getFeatureFlagTableColumns(handleOnClick), [handleOnClick]);
    const managedData = useMemo(() => getData(data), [data]);
    const groupOptions = useMemo(() => managedData
        .filter((record) => Boolean(record.children))
        .map((record) => ({
        label: record.label,
        value: record.id
    })), [managedData]);
    const filteredData = useMemo(() => (filter === undefined ?
        managedData :
        managedData.reduce((prev, curr) => {
            const lowerCaseFilter = filter.toLowerCase();
            if (curr.label.toLowerCase().includes(lowerCaseFilter)) {
                return [...prev, curr];
            }
            if (curr.children) {
                curr.children = curr.children
                    .filter((child) => child.label.toLowerCase().includes(lowerCaseFilter));
                if (curr.children.length > 0) {
                    return [...prev, curr];
                }
            }
            return prev;
        }, [])), [managedData, filter]);
    const editRecord = useMemo(() => { var _a; return (_a = data === null || data === void 0 ? void 0 : data.featureFlags.find((record) => record.name === editRecordId)) !== null && _a !== void 0 ? _a : null; }, [data, editRecordId]);
    useOnMount(() => {
        load();
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(VerticalColumnView, null,
            React.createElement(VerticalColumn, null,
                React.createElement(SearchInput, { onChange: (value) => {
                        setFilter(value);
                    } }),
                React.createElement(LoadingSpinner, { spinning: isRequested && !data }, data &&
                    React.createElement(SonarTable, { columns: columns, dataSource: filteredData, expandable: {
                            defaultExpandedRowKeys: [
                                ...filteredData
                                    .filter((record) => record.children && record.children.length > 0)
                                    .map((record) => record.id)
                            ]
                        }, id: 'feature-flags-table', pagination: false, rowKey: 'id', scroll: { x: 'max-content' } }))),
            React.createElement(VerticalColumn, null,
                React.createElement(FeatureFlagCard, { featureFlag: editRecord, groupOptions: groupOptions }))),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.ADD_CUSTOMER_FAILED, failed: ADMIN_DASHBOARD_COPY.ADD_CUSTOMER_FAILED, received: ADMIN_DASHBOARD_COPY.ADD_CUSTOMER_SUCCESS, stateSelector: overrideDefaultSelectors.state }),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.REMOVE_CUSTOMER_FAILED, failed: ADMIN_DASHBOARD_COPY.REMOVE_CUSTOMER_FAILED, received: ADMIN_DASHBOARD_COPY.REMOVE_CUSTOMER_SUCCESS, stateSelector: setDefaultSelectors.state }),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.UPDATE_FEATURE_FLAG_FAILED, failed: ADMIN_DASHBOARD_COPY.UPDATE_FEATURE_FLAG_FAILED, received: ADMIN_DASHBOARD_COPY.UPDATE_FEATURE_FLAG_SUCCESS, stateSelector: postFeatureFlagSelectors.state })));
};
